import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api/client';
import {
  SettingsPatchRequest,
  SettingsPatchResponse,
  ResetCircuitBreakerRequest,
  ResetCircuitBreakerResponse,
  ApiError,
} from '@/types/api';

export const QUERY_KEYS = {
  DASHBOARD: ['dashboard'] as const,
  STATUS: ['status'] as const,
  POSITIONS: ['positions'] as const,
  PNL: ['pnl'] as const,
  TODAY: ['today'] as const,
  WEEK: ['week'] as const,
  TRADES: ['trades'] as const,
  BUY_HISTORY: ['buy_history'] as const,
  SELL_HISTORY: ['sell_history'] as const,
  RISK: ['risk'] as const,
  HEALTH: ['health'] as const,
  SIGNALS: ['signals'] as const,
  ORDERS: ['orders'] as const,
  SETTINGS: ['settings'] as const,
  AUTH_ME: ['auth_me'] as const,
};

// Default staleTime is 15s to prevent duplicate fetches on quick tab switching.
// Real-time updates are driven by WebSocket cache invalidations, avoiding unnecessary API hammering.

export function useDashboard(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.DASHBOARD,
    queryFn: () => apiClient.getDashboard(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useStatus(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.STATUS,
    queryFn: () => apiClient.getStatus(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function usePositions(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.POSITIONS,
    queryFn: () => apiClient.getPositions(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function usePnl(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.PNL,
    queryFn: () => apiClient.getPnl(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useToday(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.TODAY,
    queryFn: () => apiClient.getToday(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useWeek(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.WEEK,
    queryFn: () => apiClient.getWeek(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 30000,
  });
}

export function useTrades(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.TRADES,
    queryFn: () => apiClient.getTrades(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useBuyHistory(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.BUY_HISTORY,
    queryFn: () => apiClient.getBuyHistory(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useSellHistory(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.SELL_HISTORY,
    queryFn: () => apiClient.getSellHistory(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useRisk(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.RISK,
    queryFn: () => apiClient.getRisk(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useHealth(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.HEALTH,
    queryFn: () => apiClient.getHealth(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useSignals(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.SIGNALS,
    queryFn: () => apiClient.getSignals(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useOrders(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.ORDERS,
    queryFn: () => apiClient.getOrders(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useSettings(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEYS.SETTINGS,
    queryFn: () => apiClient.getSettings(),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 15000,
  });
}

export function useUpdateSettings() {
  const queryClientInstance = useQueryClient();

  return useMutation<SettingsPatchResponse, Error, SettingsPatchRequest>({
    mutationFn: (updates: SettingsPatchRequest) => apiClient.patchSettings(updates),
    onSuccess: () => {
      queryClientInstance.invalidateQueries({ queryKey: QUERY_KEYS.SETTINGS });
      queryClientInstance.invalidateQueries({ queryKey: QUERY_KEYS.STATUS });
      queryClientInstance.invalidateQueries({ queryKey: QUERY_KEYS.RISK });
      queryClientInstance.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
    },
  });
}

export function useResetCircuitBreaker() {
  const queryClientInstance = useQueryClient();

  return useMutation<ResetCircuitBreakerResponse, ApiError, ResetCircuitBreakerRequest>({
    mutationFn: (req: ResetCircuitBreakerRequest) => apiClient.resetCircuitBreaker(req),
    onSuccess: () => {
      queryClientInstance.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
      queryClientInstance.invalidateQueries({ queryKey: QUERY_KEYS.RISK });
      queryClientInstance.invalidateQueries({ queryKey: QUERY_KEYS.PNL });
      queryClientInstance.invalidateQueries({ queryKey: QUERY_KEYS.STATUS });
      queryClientInstance.invalidateQueries({ queryKey: QUERY_KEYS.HEALTH });
      queryClientInstance.invalidateQueries({ queryKey: QUERY_KEYS.TODAY });
    },
  });
}
